import { XCircle } from 'lucide-react';
import { observer } from 'mobx-react';
import React from 'react';
import { useWrapRequest } from 'use-wrap-request';
import { Alert, AlertDescription } from '@components/ui/alert';
import { Button } from '@components/ui/button';
import { Checkbox } from '@components/ui/checkbox';
import { Label } from '@components/ui/label';
import { toast } from '@core/toast';
import useInfiniteScroll from '../../../components/hooks/use-scroll';
import { useSearchReducer } from '../../../components/hooks/useSearchReducer';
import { ActivityDto } from '../../../core/api/FitnessActivityClient';

import { UserContext } from '..';
import { Api } from '../../../core/api';
import { injectTSDI } from '../../../core/tsdi';
import { AddActivity } from '../../../tenant/employee/activities/add-activity';
import { FitnessActivity } from '../../../tenant/employee/activities/fitness-activity';
import { ConfirmationModal } from '../../../components/confirmation-modal';
import { Spinner } from '../../../components/spinner';

export const UserActivities = observer(() => {
    const user = React.useContext(UserContext);
    const { fitnessActivityClient } = injectTSDI(Api);
    const [searchParams, dispatch] = React.useReducer(useSearchReducer, {
        size: 30,
        page: 0,
        sort: '-timestamp'
    });

    const [fitnessActivities, setFitnessFitnessActivities] = React.useState<
        ActivityDto[]
    >([]);
    const [selectedActivityIds, setSelectedActivityIds] = React.useState<
        Set<number>
    >(new Set());
    const [bulkRejectionOpen, setBulkRejectionOpen] = React.useState(false);

    const fitnessActivityRequest = useWrapRequest(
        async (searchParams: QueryParamsDto, activities: ActivityDto[]) => {
            const result =
                await fitnessActivityClient.admin.activitySupportControllerGetActivities(
                    { userId: `${user.id}`, ...searchParams }
                );
            setFitnessFitnessActivities([
                ...activities,
                ...result.data.content
            ]);

            return { ...result.data, content: result.data.content || [] };
        },
        {}
    );

    const page = useInfiniteScroll({
        loading: fitnessActivityRequest.loading,
        distance: 200
    });

    React.useEffect(() => {
        if (
            page > 0 &&
            fitnessActivityRequest.$.totalPages &&
            fitnessActivityRequest.$.totalPages > page
        ) {
            dispatch({ type: 'page', page });
        }
    }, [page]);

    React.useEffect(() => {
        fitnessActivityRequest.request([searchParams, fitnessActivities]);
    }, [searchParams]);

    const refresh = () => {
        setSelectedActivityIds(new Set());
        setFitnessFitnessActivities([]);
        dispatch({ type: 'page', page: 0 });
        fitnessActivityRequest.request([{ ...searchParams, page: 0 }, []]);
    };

    const selectableActivityIds = fitnessActivities
        .filter((activity) => !activity.rejected)
        .map((activity) => activity.id);
    const allLoadedActivitiesSelected =
        selectableActivityIds.length > 0 &&
        selectableActivityIds.every((id) => selectedActivityIds.has(id));

    const handleSelectionChange = (activityId: number, selected: boolean) => {
        setSelectedActivityIds((currentIds) => {
            const updatedIds = new Set(currentIds);
            if (selected) {
                updatedIds.add(activityId);
            } else {
                updatedIds.delete(activityId);
            }
            return updatedIds;
        });
    };

    const handleSelectAllLoaded = (selected: boolean) => {
        setSelectedActivityIds(
            selected ? new Set(selectableActivityIds) : new Set()
        );
    };

    const rejectSelectedActivities = async () => {
        const activityIds = Array.from(selectedActivityIds);
        if (activityIds.length === 0) {
            return false;
        }

        await fitnessActivityClient.admin.activitySupportControllerRejectActivities(
            { activityIds }
        );
        toast.success(
            `${activityIds.length} ${
                activityIds.length === 1 ? 'activity' : 'activities'
            } rejected`
        );
        refresh();
        return true;
    };

    const selectAllState = allLoadedActivitiesSelected
        ? true
        : selectedActivityIds.size > 0
          ? 'indeterminate'
          : false;

    return (
        <>
            <div>
                <AddActivity onChange={refresh} />
                {fitnessActivities.length === 0 &&
                    !fitnessActivityRequest?.loading && (
                        <Alert variant="info">
                            <AlertDescription>
                                No activities found
                            </AlertDescription>
                        </Alert>
                    )}
                {fitnessActivities.length === 0 &&
                    fitnessActivityRequest?.loading && (
                        <div className="flex min-h-40 items-center justify-center">
                            <Spinner />
                        </div>
                    )}
                {selectableActivityIds.length > 0 && (
                    <div className="mb-4 flex items-center justify-between gap-4 rounded-lg border px-4 py-2">
                        <div className="flex items-center gap-2">
                            <Checkbox
                                id="select-all-loaded"
                                checked={selectAllState}
                                onCheckedChange={(checked) =>
                                    handleSelectAllLoaded(checked === true)
                                }
                            />
                            <Label htmlFor="select-all-loaded">
                                Select all loaded
                            </Label>
                        </div>
                        <div className="flex items-center gap-4">
                            <span className="text-sm text-muted-foreground">
                                {selectedActivityIds.size} selected
                            </span>
                            <Button
                                variant="outline"
                                className="border-destructive/40 text-destructive hover:text-destructive"
                                disabled={selectedActivityIds.size === 0}
                                onClick={() => setBulkRejectionOpen(true)}
                            >
                                <XCircle />
                                Reject selected
                            </Button>
                        </div>
                    </div>
                )}
                <ul
                    className={
                        fitnessActivities.length === 0
                            ? 'm-0 list-none p-0'
                            : 'm-0 list-none divide-y overflow-hidden rounded-xl border bg-card p-0'
                    }
                >
                    {fitnessActivities.map((fitnessActivity) => (
                        <FitnessActivity
                            key={fitnessActivity.id}
                            fitnessActivity={fitnessActivity}
                            onChange={refresh}
                            selected={selectedActivityIds.has(
                                fitnessActivity.id
                            )}
                            onSelectionChange={handleSelectionChange}
                        />
                    ))}
                </ul>
                {fitnessActivityRequest?.loading && (
                    <div className="flex justify-center py-4">
                        <Spinner />
                    </div>
                )}
            </div>
            <ConfirmationModal
                open={bulkRejectionOpen}
                title="Reject selected activities"
                buttonText="Reject activities"
                destructive
                submitHandler={rejectSelectedActivities}
                onModalClose={() => setBulkRejectionOpen(false)}
            >
                <p>
                    Reject {selectedActivityIds.size}{' '}
                    {selectedActivityIds.size === 1 ? 'activity' : 'activities'}
                    ? Their activity points will be removed.
                </p>
            </ConfirmationModal>
        </>
    );
});

export default UserActivities;
